/**
 * File system abstraction — uses native Electron dialogs when available,
 * falls back to browser file inputs / download links.
 */

export const isDesktop = () => !!window.electronAPI?.isDesktop;

/**
 * Open a single file via native dialog (desktop) or file input (browser).
 * @param {string} accept - File filter, e.g. '.pdf' or '.pdf,.png,.jpg'
 * @returns {Promise<{ name: string, buffer: ArrayBuffer, path: string|null } | null>}
 */
export async function openFile(accept = '.pdf') {
  if (isDesktop()) {
    return window.electronAPI.openFile(accept);
  }

  // Browser fallback: file input
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        file.arrayBuffer().then((buffer) =>
          resolve({ name: file.name, buffer, path: null })
        );
      } else {
        resolve(null);
      }
    };
    input.click();
  });
}

/**
 * Open multiple files via native dialog (desktop) or file input (browser).
 * @param {string} accept - File filter
 * @returns {Promise<Array<{ name: string, buffer: ArrayBuffer, path: string|null }> | null>}
 */
export async function openFiles(accept = '.pdf') {
  if (isDesktop()) {
    return window.electronAPI.openFiles(accept);
  }

  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) {
        resolve(null);
        return;
      }
      const results = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          buffer: await file.arrayBuffer(),
          path: null,
        }))
      );
      resolve(results);
    };
    input.click();
  });
}

/**
 * Save file via native dialog (desktop) or browser download.
 * @param {Uint8Array|ArrayBuffer} data - File content
 * @param {string} suggestedName - Default filename
 * @returns {Promise<string|null>} - Saved file path (desktop) or null (browser)
 */
export async function saveFile(data, suggestedName) {
  if (isDesktop()) {
    return window.electronAPI.saveFile(data, suggestedName);
  }

  // Browser fallback: download link
  const blob = new Blob([data], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedName;
  a.click();
  URL.revokeObjectURL(url);
  return null;
}

/**
 * Update the window title (desktop only).
 * @param {string|null} fileName - Current file name, or null for no file
 * @param {boolean} unsaved - Whether there are unsaved changes
 */
export function updateTitle(fileName, unsaved = false) {
  if (!isDesktop()) return;
  const suffix = 'Scrivar';
  if (!fileName) {
    window.electronAPI.setTitle(suffix);
  } else {
    window.electronAPI.setTitle(`${fileName}${unsaved ? '*' : ''} — ${suffix}`);
  }
}
